import { useState } from "react";
import { Mail, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * NewsletterSignup — single-field email-capture block.
 *
 * Used at the bottom of the homepage above the footer (and ready to be
 * dropped into other pages later by adjusting the `source` prop).
 *
 * Behaviour:
 *   - Lightweight client-side email regex sanity check before submit.
 *   - Disables the submit button + shows a spinner during the request.
 *   - Distinct copy for the three terminal states the API returns:
 *       subscribed → "Subscribed successfully 🎉"
 *       already    → "You are already subscribed"
 *       error      → fallback "Something went wrong, please try again"
 *   - Bilingual via `useLanguage()`; RTL is automatic because the
 *     component uses logical spacing (`gap-*`) and the input's
 *     placeholder is read by the browser in the document direction.
 *   - No tracking, no third-party services. Persists to MongoDB via
 *     `POST /api/newsletter/subscribe`.
 */

const COPY = {
  ar: {
    title: "ابقَ على اطلاع",
    description:
      "احصل على أفضل أدوات الذكاء الاصطناعي المجانية ونصائح عملية باللغة العربية",
    placeholder: "أدخل بريدك الإلكتروني",
    submit: "اشترك الآن",
    submitBusy: "جارٍ الاشتراك\u2026",
    invalid: "يرجى إدخال بريد إلكتروني صحيح",
    success: "تم الاشتراك بنجاح 🎉",
    already: "أنت مشترك بالفعل",
    rateLimited: "محاولات كثيرة، حاول لاحقًا",
    networkError: "حدث خطأ ما، يرجى المحاولة مرة أخرى",
    privacyNote: "لن نشارك بريدك مع أي طرف ثالث.",
  },
  en: {
    title: "Stay updated",
    description: "Get the best free AI tools and practical tips in Arabic",
    placeholder: "Enter your email",
    submit: "Subscribe",
    submitBusy: "Subscribing\u2026",
    invalid: "Please enter a valid email address",
    success: "Subscribed successfully 🎉",
    already: "You are already subscribed",
    rateLimited: "Too many attempts, please try again later",
    networkError: "Something went wrong, please try again",
    privacyNote: "We'll never share your email.",
  },
};

// Pragmatic email regex — broad enough for everything the backend's
// EmailStr accepts, strict enough to catch obvious typos client-side
// (missing @, spaces, missing TLD). Intentionally not RFC-perfect; the
// server is the source of truth.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function NewsletterSignup({ source = "homepage", testId = "newsletter-signup" }) {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.en;

  const [email, setEmail] = useState("");
  // P3 honeypot. Real users never see or fill this; bots that populate every
  // input do. The API silently discards submissions where it is non-empty.
  const [hpWebsite, setHpWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  // status: "idle" | "success" | "already" | "error"
  const [status, setStatus] = useState("idle");
  const [errorText, setErrorText] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;

    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setStatus("error");
      setErrorText(t.invalid);
      return;
    }

    setBusy(true);
    setStatus("idle");
    setErrorText("");

    try {
      const res = await fetch(`/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          source,
          language: lang,
          hp_website: hpWebsite,
        }),
      });

      if (res.status === 429) {
        setStatus("error");
        setErrorText(t.rateLimited);
        return;
      }
      if (!res.ok) {
        // 422 etc. — surface a friendly invalid-email message for the
        // most common case and a generic message for the rest.
        if (res.status === 422) {
          setStatus("error");
          setErrorText(t.invalid);
          return;
        }
        setStatus("error");
        setErrorText(t.networkError);
        return;
      }

      const data = await res.json();
      if (data.status === "already") {
        setStatus("already");
        setErrorText("");
      } else {
        setStatus("success");
        setErrorText("");
        // Successful first-time subscribe: clear the field. Already-
        // subscribed users keep their email visible so they can see
        // exactly which address is on the list.
        setEmail("");
      }
    } catch {
      setStatus("error");
      setErrorText(t.networkError);
    } finally {
      setBusy(false);
    }
  };

  // Show a distinct success or already-subscribed banner instead of the
  // form once we've reached a terminal state. Re-renders the form on
  // any further interaction so users can subscribe a second address if
  // they want to.
  const showFormResetCue = status === "success" || status === "already";

  return (
    <section
      className="sas-section"
      data-testid={testId}
      aria-labelledby={`${testId}-title`}
    >
      <div className="sas-container">
        <div className="max-w-2xl mx-auto text-center">
          <div
            className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-5"
            style={{
              background: "var(--sas-accent-soft)",
              color: "var(--sas-accent)",
            }}
            aria-hidden
          >
            <Mail size={20} />
          </div>
          <h2
            id={`${testId}-title`}
            className="sas-h2"
            data-testid={`${testId}-title`}
          >
            {t.title}
          </h2>
          <p
            className="sas-body mt-4 text-[15.5px] leading-[1.85] max-w-xl mx-auto"
            data-testid={`${testId}-desc`}
          >
            {t.description}
          </p>

          <form
            onSubmit={onSubmit}
            noValidate
            className="
              mt-7 mx-auto max-w-md
              flex flex-col sm:flex-row items-stretch
              gap-3
            "
            data-testid={`${testId}-form`}
          >
            <label htmlFor={`${testId}-input`} className="sr-only">
              {t.placeholder}
            </label>
            {/* Honeypot — visually hidden via the clip-based `sr-only`
                technique (never `left:-9999px`, which is scrollable
                overflow in RTL documents). aria-hidden + tabIndex=-1 keep
                it out of the accessibility tree and tab order. */}
            <div className="sr-only" aria-hidden="true">
              <label htmlFor={`${testId}-hp`}>Website (leave blank)</label>
              <input
                id={`${testId}-hp`}
                name="hp_website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={hpWebsite}
                onChange={(e) => setHpWebsite(e.target.value)}
                data-testid={`${testId}-honeypot`}
              />
            </div>
            <input
              id={`${testId}-input`}
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              placeholder={t.placeholder}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status !== "idle") setStatus("idle");
              }}
              disabled={busy}
              aria-invalid={status === "error"}
              aria-describedby={`${testId}-status`}
              className="
                sas-input flex-1 min-w-0 h-11
                text-[14.5px]
              "
              data-testid={`${testId}-input`}
            />
            <button
              type="submit"
              disabled={busy}
              className="
                sas-btn sas-btn-primary
                h-11 px-5
                whitespace-nowrap justify-center
                shrink-0
                disabled:opacity-70 disabled:cursor-not-allowed
              "
              data-testid={`${testId}-submit`}
            >
              {busy ? (
                <>
                  <Loader2 size={15} className="animate-spin" aria-hidden />
                  {t.submitBusy}
                </>
              ) : (
                t.submit
              )}
            </button>
          </form>

          {/* Status row — always rendered so the layout doesn't shift
              when a result appears. Empty string in `idle` keeps the
              field reservation visible to assistive tech via
              aria-describedby above. */}
          <div
            id={`${testId}-status`}
            role="status"
            aria-live="polite"
            className="mt-4 min-h-[22px] text-[13px]"
            data-testid={`${testId}-status`}
          >
            {status === "success" && (
              <span className="inline-flex items-center gap-1.5 text-[color:var(--sas-accent)] font-medium">
                <CheckCircle2 size={14} aria-hidden />
                {t.success}
              </span>
            )}
            {status === "already" && (
              <span className="inline-flex items-center gap-1.5 text-[color:var(--sas-ink-2)] font-medium">
                <CheckCircle2 size={14} aria-hidden />
                {t.already}
              </span>
            )}
            {status === "error" && errorText && (
              <span className="inline-flex items-center gap-1.5 text-[#b91c1c] font-medium">
                <AlertCircle size={14} aria-hidden />
                {errorText}
              </span>
            )}
          </div>

          <p
            className="mt-3 text-[11.5px] text-[color:var(--sas-ink-2)]"
            data-testid={`${testId}-privacy`}
          >
            {t.privacyNote}
          </p>

          {/* Visually hidden invalidation hint — used by screen readers
              to confirm the form was reset after a successful submit. */}
          <span className="sr-only">
            {showFormResetCue ? "form reset" : ""}
          </span>
        </div>
      </div>
    </section>
  );
}
